import type { RotationPhasePayload, RotationSafetyPayload } from '@/hooks/useWebSocket';
import type { CoverageRow } from './coverageTypes';

/**
 * THE WORDS AND COLOURS CONTINUOUS COVERAGE IS READ IN.
 *
 * Extracted verbatim from `MissionCoveragePanel.tsx`. Every map here is a vocabulary an operator
 * reads under time pressure, and several of them are deliberately paired with a severity the
 * alert list files for the same event — the reasoning for each is kept with the entry it explains.
 */

/**
 * The dominant reason nobody can relieve the station, in two or three words.
 *
 * A COUNT IS THE WRONG NUMBER even when it is right: "3 CHECKED" tells an operator only that the
 * system tried. What they need at a glance is whether to go and charge something or go and look at
 * a dock, which is the reason SHAPE — and the server already computed it, one code per aircraft.
 */
export const REJECTION_LABEL: Record<string, string> = {
  inactive: 'STOOD DOWN',
  airborne: 'ALREADY FLYING',
  no_dock: 'NO DOCK',
  not_ready: 'NOT READY',
  unknown_to_fleet: 'UNKNOWN AIRCRAFT',
  // W4: the gate PASSED this aircraft and the rotation refused it (§12). Worded so it does not
  // read as a Fleet verdict — an operator may be looking at a GO badge for it on the fleet page.
  no_battery_percent: 'NO BATTERY %',
};

/**
 * What an operator is shown for each phase of a launch (§9), and each named state (§13).
 *
 * `scheduled` maps to null: it is the state every forecast sits in and it is what a reader already
 * assumes, so it earns no pixels on what is already the densest row in the panel. Only a launch
 * that is actually under way, or a state that wants attention, gets a chip.
 *
 * The three terminal-ish W5 phases are here so this map is total over the union — a phase added to
 * the server without a word for it should fail the build, not render blank.
 */
const PHASE_LABEL: Readonly<Record<RotationPhasePayload['phase'], string | null>> = {
  scheduled: null,
  preparing: 'PRE-LAUNCH CHECK',
  launching: 'LAUNCHING',
  transit: 'IN TRANSIT',
  on_station: 'ON STATION',
  confirming: 'CONFIRMING',
  // W8's three, and they are THREE SITUATIONS rather than one repeated.
  //
  // `handover_done` is no longer the end. It means ownership has moved and the relief is holding
  // the station from the handover slot - one separation ABOVE where it belongs - while the
  // outgoing aircraft clears the area, which can last up to five minutes. That fact is the whole
  // reason the label says RELIEF HIGH: an operator reading a bare "HANDOVER DONE" would have no
  // way to know an aircraft is 5 m off its assigned altitude and that the platform is still
  // waiting on something.
  handover_done: 'HANDOVER DONE · RELIEF HIGH',
  // The manoeuvre, worded as one rather than as a phase name: the station has already changed
  // hands, so this is the relief coming down to the station altitude, not a landing.
  descending: 'RELIEF DESCENDING',
  descent_complete: 'ROTATION COMPLETE',
  // #564's two, and they are the only phases in this map during which the station is EMPTY.
  //
  // Worded as what is happening to the aircraft rather than as phase names, because on a rotation
  // of one those two facts are the same fact: there is no relief holding anything while this runs,
  // so the chip tells an operator both where the aircraft is and why the coverage badge beside it
  // says GAP.
  //
  // NOT "RETURNING TO SWAP", which was the first wording. A rotation of one is deliberately allowed
  // on a plain CHARGING dock, and the panel does not know which kind this is - so "SWAP" would
  // promise the five-minute version of a gap that may be half an hour. This chip is currently the
  // only thing on screen that hints at the duration, which makes the wrong hint worse than none.
  returning: 'RETURNING TO DOCK',
  resupplying: 'IN DOCK · RESUPPLYING',
};

/**
 * §13's named states, in the operator's words rather than the wire's.
 *
 * `severity` decides the colour, and `approval_required` is the reason it is not simply "amber =
 * bad": that state is the platform WAITING FOR A PERSON, which is an instruction rather than a
 * fault. It is the most actionable thing this panel can say — the launch is frozen, and only a
 * human can unfreeze it — so it is drawn in amber to be noticed, with wording that asks rather
 * than reports.
 */
const PHASE_REASON: Readonly<Record<
  NonNullable<RotationPhasePayload['phase_reason']>,
  { label: string; severity: 'wait' | 'caution' | 'fault' }
>> = {
  approval_required: { label: 'APPROVAL REQUIRED', severity: 'wait' },
  // #547's pair. `bay_hold` is `approval_required`'s machine-side twin and gets its severity: the
  // launch is frozen, nothing has gone wrong, and it clears by itself - so `wait`, and the same
  // `wait` the alert list does NOT page anybody about, which is the pairing this file already
  // records for `separation_hold`.
  //
  // `bay_hold_expired` is deliberately not "LAUNCH FAILED · BAY". That state means a tray that
  // never opened and sends an operator to look at the mechanism; this one means a bay that was
  // busy for longer than it said it would be. Same hardware, different thing wrong with it.
  bay_hold: { label: 'BAY HOLD', severity: 'wait' },
  bay_hold_expired: { label: 'BAY NEVER FREED', severity: 'fault' },
  // W8's two. Neither is `wait` - nobody is being waited on, the rotation has finished
  // imperfectly - and neither means the station is uncovered: the relief is on it in both cases,
  // from an altitude slot inside the confirmation band. So the wording says what did not happen
  // rather than implying a gap in coverage.
  //
  // THE SEVERITIES MATCH THE ALERTS THE SAME EVENTS RAISE, and that is the whole reason `caution`
  // exists. `rotationAlertFor` files `descent_deferred` as a `warning` and `descent_failed` as a
  // `critical` (D7-W8), and this panel and the alert list are two surfaces an operator uses
  // together - the mistake this file already records at the COP's `handover` colour. Painting a
  // deferred descent the same red as RELIEF LOST ENROUTE while `/alerts` shows it amber is that
  // mistake again.
  descent_deferred: { label: 'DESCENT DEFERRED', severity: 'caution' },
  descent_failed: { label: 'RELIEF DID NOT DESCEND', severity: 'fault' },
  // "We commanded and we do not know" — never filed as a failure, because the aircraft may be
  // flying. The wording has to make a human go and look.
  // Shortened from "LAUNCH UNRESOLVED — CONFIRM VISUALLY": at ~275 px that was 84% of the
  // collapsed row on a 360 px viewport on its own, and this row already carries up to five other
  // chips. The instruction to go and look survives in the expanded block and the live region,
  // which are where somebody acting on it will be reading anyway.
  launch_unresolved: { label: 'LAUNCH UNRESOLVED', severity: 'fault' },
  replacement_refused: { label: 'REPLACEMENT REFUSED', severity: 'fault' },
  launch_failed_bay: { label: 'LAUNCH FAILED · BAY', severity: 'fault' },
  replacement_lost_enroute: { label: 'RELIEF LOST ENROUTE', severity: 'fault' },
  handover_unconfirmed: { label: 'HANDOVER UNCONFIRMED', severity: 'fault' },
  separation_hold: { label: 'SEPARATION HOLD', severity: 'wait' },
  return_at_risk: { label: 'RETURN AT RISK', severity: 'fault' },
  recovery_blocked: { label: 'RECOVERY BLOCKED', severity: 'fault' },
  recovery_failed_swap: { label: 'RECOVERY FAILED · SWAP', severity: 'fault' },
  dji_rth_takeover: { label: 'DJI RTH TAKEOVER', severity: 'fault' },
  // #564's four, all `fault`, and the severities match the alerts the same events raise — every
  // one of them is filed CRITICAL by `rotationAlertFor`, for the reason this map already records
  // for W8's pair: the panel and the alert list are two surfaces read together.
  //
  // None of these is a `wait`, even though two of them are the platform waiting. The distinction
  // this file draws for `bay_hold` — "the launch is frozen, nothing has gone wrong" — does not
  // survive here: the thing being waited on is the only aircraft the rotation has, and the station
  // is uncovered for the whole of the wait.
  self_swap_recall_failed: { label: 'NOT COMING HOME', severity: 'fault' },
  self_swap_recovery_blocked: { label: 'RELAUNCH REFUSED', severity: 'fault' },
  self_swap_recovery_failed: { label: 'NOT MADE READY', severity: 'fault' },
  self_swap_station_unconfirmed: { label: 'BACK UP · NOT SETTLED', severity: 'fault' },
  // #569's one. `caution` and not `fault`, matching the `warning` `rotationAlertFor` files for it -
  // the pairing this map already records for W8's two and #564's four. Nothing went wrong: the
  // window ended, or an operator suspended the rotation, while the cycle was in the air.
  //
  // Not `wait` either, which is reserved here for the platform being BLOCKED on somebody. Nobody is
  // being waited on - the rotation is over. It is the only `caution` that does NOT leave the
  // station covered, which is why the sentence block below had to stop keying its colour on
  // "not a wait".
  //
  // NOT "STATION STOOD DOWN", WHICH IS ALREADY TAKEN ON THIS PANEL (product review). The rotation
  // member chips render `· STOOD DOWN` for an aircraft an operator removed from the rotation, a few
  // hundred pixels below this one and often on screen at the same time. Two different meanings for
  // one phrase, and under time pressure the collision points an operator at the rotation membership
  // instead of at the window.
  coverage_stood_down: { label: 'ROTATION ENDED', severity: 'caution' },

  // #703'S TWO, AND THE SEVERITIES ARE THE ALERTS' (this file's own rule, three paragraphs of it
  // above). The successful recall files a WARNING, because the platform acted correctly and in
  // time and what deserves attention is the consequence, so `caution`. The failed one files a
  // CRITICAL, because nothing is bringing the aircraft home, so `fault`. Without these entries
  // `phaseReason`'s fallback rendered BOTH as a red `fault` chip, which contradicted the amber
  // alert for the successful case - exactly the panel/alert divergence this map exists to stop.
  //
  // "RECALLED", not "RETURNING": the recall is a command that went out, and this chip is the
  // record that it went out unapproved. Whether the aircraft is home is a different question the
  // panel answers elsewhere.
  last_resort_recall: { label: 'RECALLED · NO RELIEF', severity: 'caution' },
  last_resort_recall_failed: { label: 'RECALL REFUSED', severity: 'fault' },
};

/**
 * `PHASE_REASON`, but SAFE against a value this bundle has never heard of.
 *
 * The phase arrives on a socket frame that the client does not validate, so an unrecognised string
 * reaches the map as a miss and `.severity` on `undefined` throws DURING RENDER — and there is no
 * `error.tsx` anywhere under `(dashboard)`, so that white-screens the whole mission page while an
 * aircraft is in transit. `StateBadge` carries the identical reasoning for the identical reason;
 * every other map read here already has a `?? fallback`, and these two did not.
 *
 * It is a live risk rather than a theoretical one: W5 adds states, and during a rolling deploy the
 * API goes first while an operator's tab holds this bundle.
 *
 * UNKNOWN FAILS LOUD, not quiet: an unrecognised §13 state is rendered in red with its wire name
 * made readable. Showing nothing would hide a condition the platform is trying to report.
 */
export function phaseReason(
  reason: NonNullable<RotationPhasePayload['phase_reason']>,
): { label: string; severity: 'wait' | 'caution' | 'fault' } {
  return PHASE_REASON[reason]
    ?? { label: String(reason).replace(/_/g, ' ').toUpperCase(), severity: 'fault' };
}

/**
 * The §13 states that leave the station MANNED - the axis the detail block and the last-rotation
 * block both turn on, in one place so they cannot disagree.
 *
 * W8's two are here because the relief is on the station in both cases, from an altitude slot
 * inside the confirmation band; the rotation finished badly, the coverage did not fail. That fact
 * decides a colour, and it is NOT the same question as the chip's severity: `descent_failed` is a
 * `fault` AND covered, so keying the sentence on severity paints "The station is covered" red.
 */
export const STATION_STILL_COVERED: ReadonlySet<string> = new Set(['descent_deferred', 'descent_failed']);

/**
 * The sentence a four-word red chip cannot carry, for the two states where the missing sentence
 * is the difference between "an aircraft is in trouble" and "the height is wrong" (W8, #510).
 *
 * Both of these leave the station COVERED - the relief is on it, inside the confirmation band -
 * and the chip alone reads like a fault of the same class as RELIEF LOST ENROUTE. That fact lived
 * only in `rotationAlertFor`'s alert message, which renders on `/alerts`; the mission page, which
 * is where a rotation is actually watched, said four red words and nothing else.
 *
 * The phase chip's other named states do not need this - LAUNCH FAILED · BAY means what it says -
 * so this is deliberately a short map rather than a sentence for every reason.
 *
 * Named for the question rather than for W8 (#547): the two states below it are about a bay, not a
 * descent, and a map still called DESCENT_SENTENCE holding them would be the kind of name that
 * outlives its own reason.
 */
const REASON_SENTENCE: Partial<Record<
  NonNullable<RotationPhasePayload['phase_reason']>, string
>> = {
  descent_deferred:
    'The station is covered. The relief is holding it above the assigned station altitude because '
    + 'the aircraft it relieved has not cleared the area; that is the aircraft to look at.',
  descent_failed:
    'The station is covered. The relief was commanded down to the station altitude and the '
    + 'platform cannot confirm it arrived, so it is holding the station from the handover slot.',
  // #547's pair, and they need the sentence for the opposite reason W8's two do. BAY HOLD is the
  // rarest thing on this chip: a named state that means nothing is wrong. Two words in a row of
  // otherwise-red chips reads as a fault unless it says it is not one.
  bay_hold:
    'Nothing has gone wrong. The relief cannot leave its bay yet - the dock is swapping a battery, '
    + 'or the aircraft is still charging to the launch minimum - so the launch is being held '
    + 'rather than refused, and it goes as soon as the bay is free.',
  bay_hold_expired:
    'The relief\'s bay was waited on and never became launchable, so the rotation was given up. '
    + 'Nothing is on its way to the station: the dock is the thing to look at, and a new '
    + 'replacement needs approving once it is dealt with.',
  // #703'S TWO, AND THEY EARN A SENTENCE MORE THAN ANYTHING ELSE ON THIS MAP. Every other state
  // here reports something the platform OBSERVED. These two report something it DID, without
  // being asked - and a chip reading RECALLED · NO RELIEF, with no explanation under it, leaves
  // an operator to guess who ordered it.
  last_resort_recall:
    'Nobody approved this. The aircraft holding the station reached the battery it has to start '
    + 'home on and no relief was coming, so the platform recalled it rather than let its own '
    + 'low-battery RTH take over and land it beside the tray instead of on it. THE STATION IS '
    + 'UNCOVERED until something relieves it. Whatever stopped the relief is named in its own '
    + 'alert.',
  last_resort_recall_failed:
    'The aircraft holding the station reached the battery it has to start home on, and the '
    + 'platform could not command it home - usually an airframe the orchestrated return does not '
    + 'drive. It is flying its own return logic, or none, at the battery where the aircraft\'s '
    + 'own RTH takes over. THE STATION IS UNCOVERED. Take the sticks and bring it down.',
  // #564's four all get a sentence, which is a departure from this map's own rule that a state
  // whose chip says what it means does not need one. The rule holds for a rotation of two, where
  // the reader can see a second aircraft on the panel and work out what is covering the station.
  // Here there is one chip, one aircraft, and the thing an operator most needs to know is not on
  // the chip at all: whether anything is holding the station. Nothing is, in all four.
  self_swap_recall_failed:
    'This rotation has ONE aircraft. It was told to come home to swap and either could not be told '
    + 'or has not arrived. The station is uncovered, and there is no second airframe to send — it '
    + 'is flying its own return logic, or none. Find it and bring it home.',
  self_swap_recovery_blocked:
    'This rotation has ONE aircraft. It is DOWN in its dock, and the attempt to send it back up was '
    + 'refused. The platform retries every minute, so a dock still finishing a swap or a charge '
    + 'clears on its own — but a tray fault or a lapsed approval will not. Check the refusal below '
    + 'before assuming it is the battery.',
  self_swap_recovery_failed:
    'This rotation has ONE aircraft. It is down, and its dock has already run past the time it said '
    + 'it needed — a swap that did not complete, or a charge that is not climbing. The platform '
    + 'keeps retrying and a dock that recovers brings the station back on its own, but it has '
    + 'stopped behaving as designed: go and look at it.',
  self_swap_station_unconfirmed:
    'This rotation has ONE aircraft. It was resupplied and relaunched, reached the station area and '
    + 'has not settled on it, so the station is still reported uncovered. Check its altitude and '
    + 'its hold against the station band.',
  // #569's sentence is NOT here. It is the one reason whose wording depends on WHICH coverage state
  // stood the station down, because the remedy differs and only one of the two exists on this
  // panel - see `reasonSentence`.
};

/**
 * `REASON_SENTENCE`, with the sentences that depend on there being a SECOND aircraft (#564).
 *
 * `approval_required` has no shared sentence at all, because for a rotation of two the chip says
 * everything: a launch is frozen until somebody approves it. For a rotation of one the same chip
 * hides the decision that actually matters — what is frozen is a RECALL, and approving it is what
 * empties the station. That is the single most consequential press on this panel and it was the
 * one with no explanation on screen.
 */
export function reasonSentence(
  reason: NonNullable<RotationPhasePayload['phase_reason']>,
  selfSwap: boolean,
  /** The live coverage state, which is what decides #569's remedy. Null before it is known. */
  coverageState: string | null = null,
): string | undefined {
  // #569. ONE NAMED STATE, TWO REMEDIES, AND ONLY ONE OF THEM EXISTS ON THIS PANEL.
  //
  // The server files one reason for both coverage states on purpose (D3): an operator's SITUATION
  // is identical either way, and which of the two it was is detail. The product review found that
  // their REMEDY is not identical at all, and that a shared sentence naming both was actively
  // dangerous:
  //
  //   - `suspended` renders the rotation controls, so Resume is one press away.
  //   - `stopped` HIDES that whole block (`attached && liveState !== 'stopped'`), the server refuses
  //     suspend/resume on a stopped row, and the tracker re-derives `stopped` from `window_end` every
  //     sweep - so there is no Resume to find. An operator told to "resume the rotation", finding no
  //     such control, reaches for the only coverage control left on screen: Detach, whose own
  //     caption says a mission still running is COMPLETED by it.
  //
  // So the head is shared and the last sentence is not.
  if (reason === 'coverage_stood_down') {
    // Deliberately does NOT claim the resupply finished or that the aircraft is ready. The gate
    // fires on the first sweep after touchdown, which is typically minutes before a swap completes;
    // the dock carries on under its own control either way.
    const head = 'Nothing has gone wrong. The station was stood down while this cycle was in the '
      + 'air, so the aircraft came home to its dock and has deliberately NOT been sent back up. '
      + 'Nothing is holding the station.';
    if (coverageState === 'suspended') {
      return `${head} This rotation is suspended: press Resume rotation below to start planning `
        + 'again. Until then nothing will go back up.';
    }
    if (coverageState === 'stopped') {
      return `${head} The coverage window has ENDED, which is terminal - there is no Resume, and `
        + 'extending the window does not revive it. Covering this station again means detaching '
        + 'coverage and attaching it afresh; read what Detach says before pressing it.';
    }
    return `${head} It stays that way until this rotation is resumed or coverage is attached afresh.`;
  }

  if (selfSwap && reason === 'approval_required') {
    return 'This rotation has ONE aircraft. Approving recalls it to its dock to be resupplied:'
      + ' the station is uncovered from the moment it turns for home until it is back up.'
      + ' Until then nothing is commanded — it holds the station on the reserve it was going to'
      + ' come home on.';
  }
  return REASON_SENTENCE[reason];
}

/** `PHASE_LABEL`, same guard. An unknown phase renders its own name rather than an empty chip. */
export function phaseLabel(phase: RotationPhasePayload['phase']): string | null {
  if (phase in PHASE_LABEL) return PHASE_LABEL[phase];
  return String(phase).replace(/_/g, ' ').toUpperCase();
}

/** Is a launch actually under way, i.e. past the point where "approved" is the interesting fact? */
export function launchUnderWay(phase: RotationPhasePayload | null): boolean {
  return phase !== null && phase.phase !== 'scheduled';
}

export function dominantRejection(
  rejected: ReadonlyArray<{ rejection: string }>,
): string | null {
  if (rejected.length === 0) return null;
  const counts = new Map<string, number>();
  for (const r of rejected) counts.set(r.rejection, (counts.get(r.rejection) ?? 0) + 1);
  let top = rejected[0].rejection;
  for (const [kind, n] of counts) if (n > (counts.get(top) ?? 0)) top = kind;
  const label = REJECTION_LABEL[top] ?? top.replace(/_/g, ' ').toUpperCase();
  return counts.size > 1 ? `${label} +${counts.size - 1}` : label;
}

/** The safety states of §13, in the operator's words and the platform's status tokens. */
export const SAFETY_PRESENTATION: Readonly<Record<RotationSafetyPayload['state'], { label: string; className: string }>> = {
  dji_rth_takeover: { label: 'DJI RTH TAKEOVER', className: 'text-status-red-fg' },
  critical: { label: 'BATTERY CRITICAL', className: 'text-status-red-fg' },
  // Amber, not red: an unreadable battery is missing evidence, not a measured emergency, and
  // painting it the same colour as one would spend the red on the wrong row.
  unknown_battery: { label: 'BATTERY UNKNOWN', className: 'text-status-amber-fg' },
};

/**
 * The six coverage states, in the operator's words and the platform's THEME TOKENS.
 *
 * `text-status-*-fg` rather than raw `text-green-400`: the app has a real light theme whose
 * tokens are darkened specifically so status text survives a light card. A raw 400-weight
 * palette colour over a 20% wash is around 1.3:1 there — the GAP badge, the single most
 * important element on this panel, would be effectively invisible.
 *
 * `pending_station` reads ATTACHED — NOT RUNNING, not "en route". Stage 1 never moves this
 * column (the tracker that does is #249), so every freshly attached mission sits here — and a
 * blue "EN ROUTE TO STATION" over a draft mission would claim an aircraft is flying to a
 * station when nothing has been commanded at all.
 */
export const STATE_PRESENTATION: Readonly<Record<CoverageRow['state'], { label: string; className: string }>> = {
  // Blue, the shared vocabulary's planned/standby — NOT grey. "will run" and "is over" are the
  // pair most likely to be confused at a glance, and grey for both left the word as the only
  // thing telling them apart. `StatusBadge` made the same call for CANCELLED vs COMPLETED.
  // NOT "ATTACHED — NOT RUNNING" any more. Stage 1 chose that wording when nothing could ever
  // move this column, so `pending_station` could only mean a mission that had not launched.
  // #249 invalidated the premise: the tracker returns `pending_station` for an IN-PROGRESS
  // mission whose aircraft is en route, or is on station but has not finished its dwell. The
  // old label then sat beside the sentence "On station for 4s of the 10s required", asserting
  // the mission was not running while the line next to it described an aircraft on station.
  // The new wording is true in both cases: nobody is holding the station yet.
  pending_station: { label: 'STATION NOT MANNED', className: 'bg-status-blue/20 text-status-blue-fg' },
  covered:         { label: 'COVERED',                className: 'bg-status-green/20 text-status-green-fg' },
  // Amber, matching the COP's row (#392 logs the old blue as a defect; the COP shipping amber
  // would have made the same state two colours on two surfaces an operator uses together —
  // and blue is what `pending_station` wears on THIS panel).
  handover:        { label: 'HANDOVER',               className: 'bg-status-amber/20 text-status-amber-fg' },
  gap:             { label: 'GAP',                    className: 'bg-status-red/20 text-status-red-fg' },
  suspended:       { label: 'SUSPENDED',              className: 'bg-status-amber/20 text-status-amber-fg' },
  stopped:         { label: 'STOPPED',                className: 'bg-status-gray/20 text-status-gray-fg' },
};

/**
 * Aircraft states that mean "this airframe cannot take a turn on the station right now".
 *
 * Advisory, never a block: readiness is the server's judgement (`aircraft-readiness`), and
 * this surface has only `drones.status`. What it must not do is render OFFLINE and MAINTENANCE
 * in the same muted grey as IDLE on a control that commits a station to being manned.
 */
export const UNAVAILABLE_DRONE_STATUSES: ReadonlySet<string> = new Set(['offline', 'maintenance']);

/**
 * THE ARM-THEN-CONFIRM DWELL, shared by every destructive control in this feature.
 *
 * A second press inside this window is a double-click, not a confirmation. It lives here rather
 * than in the panel because the controls it guards no longer live in one file: rotate-now is in
 * the action card, detach and the airborne override in the lifecycle drawer. One number, one
 * meaning — `MissionCommitStrip` still declares its own copy for its own controls.
 */
export const CONFIRM_DWELL_MS = 600;
